import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MilestoneName } from '@models';
import { KpiCommonFunctionsService } from 'app/services/kpi-common-functions/kpi-common-functions.service';

import { Kpi, KpiKeys } from '../../../models/Kpi';

@Component({
  selector: 'kpi',
  templateUrl: './kpi.component.html',
  styleUrl: './kpi.component.scss'
})
export class KpiComponent implements OnInit, OnDestroy {
  private kpiCommonFunctionsService: KpiCommonFunctionsService = inject(KpiCommonFunctionsService);
  @Input() kpi!: Kpi;
  @Input() kpiKey!: KpiKeys;
  @Input() tooltipEnabled!: boolean;
  @Input() isSelected = false; // Track if this KPI is selected from parent
  @Input() keyMilestones?: MilestoneName[];
  @Output() kpiSelected = new EventEmitter<KpiKeys>(); // Emit when this KPI is clicked
  @Output() tooltipWasOpened = new EventEmitter<boolean>();

  displayValue?: number | string;
  displayUnit!: '%' | '';
  displaySecondaryValue?: string;

  // Tooltip state management
  tooltipOpen = false;
  private tooltipHideTimeoutId: number | null = null;
  private isKpiHovered = false;
  private isTooltipHovered = false;

  ngOnInit() {
    this.displayValue = this.kpiCommonFunctionsService.getKpiDisplayValue(this.kpi, this.kpiKey);
    this.displayUnit = this.kpiCommonFunctionsService.getKpiDisplayUnit(this.kpi.type);
    this.displaySecondaryValue = this.kpiCommonFunctionsService.getKpiDisplaySecondaryValue(
      this.kpi,
      this.kpiKey
    );
  }

  ngOnDestroy(): void {
    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
    }
  }

  onKpiMouseEnter(): void {
    if (!this.tooltipEnabled) return;

    this.isKpiHovered = true;

    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
      this.tooltipHideTimeoutId = null;
    }

    this.tooltipOpen = true;
  }

  onKpiMouseLeave(): void {
    if (!this.tooltipEnabled) return;

    this.isKpiHovered = false;
    this.scheduleTooltipHide();
  }

  onTooltipHover(): void {
    this.isTooltipHovered = true;

    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
      this.tooltipHideTimeoutId = null;
    }
  }

  onTooltipLeave(): void {
    this.isTooltipHovered = false;
    this.scheduleTooltipHide();
  }

  private scheduleTooltipHide(): void {
    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
      this.tooltipHideTimeoutId = null;
    }

    // Don't hide if either KPI or tooltip is hovered
    if (this.isKpiHovered || this.isTooltipHovered) {
      return;
    }

    this.tooltipHideTimeoutId = window.setTimeout(() => {
      this.tooltipOpen = false;
      this.tooltipHideTimeoutId = null;
    }, 150);
  }

  handleTooltipOpened() {
    if (this.tooltipEnabled) {
      this.tooltipWasOpened.emit(true);
    }
  }

  onKpiClick(event: Event) {
    event.stopPropagation(); // Prevent event bubbling
    this.kpiSelected.emit(this.kpiKey); // Emit the KPI key to parent
  }

  get displayValueValid(): boolean {
    return this.displayValue !== undefined; // Can be 0
  }
}
